import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Save, RefreshCw, Download, AlertTriangle, Bot, Shield, TrendingUp, CircleAlert, Target } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip
} from 'recharts';

/* ── Markdown-aware synthesis renderer ── */
const SynthesisRenderer = ({ text }) => {
  // Detect decision badge
  const detectDecision = (t) => {
    const upper = t.toUpperCase();
    if (upper.includes('ACHAT') || upper.includes('ACHETER') || upper.includes('INVESTIR')) return { label: 'ACHAT', color: 'bg-green-500/20 text-green-400 border-green-500/30' };
    if (upper.includes('REFUSER') || upper.includes('ÉVITER') || upper.includes('NE PAS INVESTIR') || upper.includes('VENDRE')) return { label: 'REFUSER', color: 'bg-red-500/20 text-red-400 border-red-500/30' };
    if (upper.includes('ATTENDRE') || upper.includes('SURVEILLER') || upper.includes('PRUDENCE')) return { label: 'ATTENDRE', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    return null;
  };

  // Parse inline **bold**
  const renderInline = (line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <span key={i}>
        {parts.map((p, j) =>
          p.startsWith('**') && p.endsWith('**')
            ? <strong key={j} className="text-white font-semibold">{p.slice(2, -2)}</strong>
            : p
        )}
      </span>
    );
  };

  // Split into sections by ## or ### headings
  const lines = text.split('\n');
  const sections = [];
  let current = { title: '', lines: [] };

  lines.forEach(line => {
    const h2 = line.match(/^#{2,3}\s+(.+)/);
    if (h2) {
      if (current.title || current.lines.length) sections.push(current);
      current = { title: h2[1].replace(/\*\*/g, ''), lines: [] };
    } else if (line.trim()) {
      current.lines.push(line);
    }
  });
  if (current.title || current.lines.length) sections.push(current);

  // Map section titles to visual config
  const getSectionStyle = (title) => {
    const t = title.toLowerCase();
    if (t.includes('force') || t.includes('point fort') || t.includes('atout'))
      return { icon: <TrendingUp className="w-4 h-4" />, accent: 'border-l-green-500', bg: 'bg-green-500/5' };
    if (t.includes('risque') || t.includes('faiblesse') || t.includes('menace'))
      return { icon: <CircleAlert className="w-4 h-4" />, accent: 'border-l-red-400', bg: 'bg-red-500/5' };
    if (t.includes('décision') || t.includes('suggestion') || t.includes('recommandation') || t.includes('verdict'))
      return { icon: <Shield className="w-4 h-4" />, accent: 'border-l-cap-blue', bg: 'bg-cap-blue/5' };
    if (t.includes('condition') || t.includes('invalid') || t.includes('sortie') || t.includes('signal'))
      return { icon: <Target className="w-4 h-4" />, accent: 'border-l-yellow-500', bg: 'bg-yellow-500/5' };
    return { icon: null, accent: 'border-l-white/20', bg: 'bg-white/[0.02]' };
  };

  const decision = detectDecision(text);

  return (
    <div className="space-y-4">
      {decision && (
        <div className="flex justify-center mb-2">
          <span className={`px-5 py-2 rounded-full border font-display font-bold text-sm tracking-wide ${decision.color}`}>
            {decision.label}
          </span>
        </div>
      )}
      {sections.map((sec, i) => {
        if (!sec.title && sec.lines.length === 0) return null;
        const style = sec.title ? getSectionStyle(sec.title) : { icon: null, accent: 'border-l-transparent', bg: '' };
        return (
          <div key={i} className={`rounded-lg border-l-[3px] ${style.accent} ${style.bg} p-4`}>
            {sec.title && (
              <h4 className="text-white font-display font-bold text-sm mb-2 flex items-center gap-2">
                {style.icon}
                {sec.title}
              </h4>
            )}
            <div className="text-cap-text-secondary text-sm leading-relaxed space-y-1.5">
              {sec.lines.map((line, j) => {
                const trimmed = line.replace(/^[-•*]\s*/, '');
                const isList = /^[-•*]\s/.test(line);
                if (isList) {
                  return (
                    <div key={j} className="flex gap-2 items-start">
                      <span className="text-cap-text-muted mt-0.5 flex-shrink-0">•</span>
                      <span>{renderInline(trimmed, j)}</span>
                    </div>
                  );
                }
                return <p key={j}>{renderInline(line, j)}</p>;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AnalyseResults = ({ model, analysis, financialData, onBack, onRedo, onSave, apiUrl, authHeader }) => {
  const [aiSynthesis, setAiSynthesis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const resultsRef = useRef(null);

  // Load cached synthesis from backend on mount (per analysis_id, user-scoped)
  useEffect(() => {
    if (!analysis?.id || !authHeader) return;
    let cancelled = false;
    axios.get(`${apiUrl}/ai-cache/synthesis/${analysis.id}`, { headers: authHeader() })
      .then(r => { if (!cancelled && r.data?.text) setAiSynthesis(r.data.text); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [analysis?.id, apiUrl, authHeader]);

  const rapidNums = model.meta.mode_rapide.sections;
  const activeSections = useMemo(() => {
    const sections = model[analysis.mode] || [];
    if (analysis.isRapid) return sections.filter(s => rapidNums.includes(s.numero));
    return sections;
  }, [model, analysis.mode, analysis.isRapid, rapidNums]);
  const allSections = model[analysis.mode] || [];

  const verdict = useMemo(() => {
    const pct = analysis.totalScore?.percent || 0;
    const v = model.meta.verdicts;
    if (pct >= v.fort.seuil * 100) return { label: v.fort.label, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20' };
    if (pct >= v.correct.seuil * 100) return { label: v.correct.label, color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' };
    return { label: v.insuffisant.label, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' };
  }, [analysis.totalScore, model.meta.verdicts]);

  const radarData = useMemo(() =>
    activeSections.map(s => ({
      section: s.id,
      name: s.titre,
      score: analysis.sectionScores?.[s.id]?.percent || 0,
      fullMark: 100,
    })),
  [activeSections, analysis.sectionScores]);

  const generateSynthesis = useCallback(async () => {
    if (aiSynthesis) return; // already loaded (server-cached on first call)

    setAiLoading(true);
    try {
      const scoresMap = {};
      activeSections.forEach(s => {
        const sc = analysis.sectionScores?.[s.id];
        if (sc) scoresMap[s.titre] = `${sc.raw}/${sc.maxRaw} (${sc.percent}%)`;
      });

      const headers = authHeader ? authHeader() : {};
      const { data } = await axios.post(`${apiUrl}/ai/synthesis`, {
        ticker: analysis.ticker,
        company_name: analysis.companyName,
        mode: analysis.mode === 'qualite' ? 'Qualité Long Terme' : 'Fort Potentiel',
        scores_by_section: scoresMap,
        financial_data: analysis.financialDataSnapshot || financialData || {},
      }, { headers });

      setAiSynthesis(data.response);
      // Persist to backend cache (user-scoped via auth)
      axios.put(`${apiUrl}/ai-cache/synthesis/${analysis.id}`, { text: data.response }, { headers })
        .catch(() => {});
    } catch (err) {
      toast.error('Erreur IA : ' + (err.response?.data?.detail || 'service indisponible'));
    }
    setAiLoading(false);
  }, [apiUrl, analysis, activeSections, financialData, aiSynthesis, authHeader]);

  const handleSave = () => { onSave(analysis); toast.success('Analyse sauvegardée'); };

  const handleExportPDF = async () => {
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const el = resultsRef.current;
      if (!el) return;
      toast.info('Génération du PDF...');
      const canvas = await html2canvas(el, { backgroundColor: '#0d0f14', scale: 1.5 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfW, pdfH);
      pdf.save(`CAP_OS_Analyse_${analysis.ticker}_${analysis.date}.pdf`);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur export PDF'); }
  };

  const getAppréciation = (pct) => {
    if (pct >= 75) return { icon: '\u{1F7E2}', label: 'Excellent' };
    if (pct >= 50) return { icon: '\u{1F7E1}', label: 'Correct' };
    return { icon: '\u{1F534}', label: 'Insuffisant' };
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="analyse-results">
      <Button variant="ghost" onClick={onBack} className="text-cap-text-secondary hover:text-white mb-4" data-testid="results-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Retour
      </Button>

      <div ref={resultsRef} className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-cap-emerald/20 flex items-center justify-center">
            <span className="font-display font-bold text-cap-emerald text-lg">{analysis.ticker?.slice(0, 2)}</span>
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">{analysis.companyName}</h1>
            <p className="text-cap-text-muted">
              {analysis.ticker} | {analysis.mode === 'qualite' ? 'Qualité Long Terme' : 'Fort Potentiel'}
              {analysis.isRapid ? ' (Rapide)' : ''} | {analysis.date}
            </p>
          </div>
        </div>

        {/* Score Summary */}
        <div className={`rounded-2xl border p-6 ${verdict.bg}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="text-center">
              <p className="text-cap-text-muted text-sm mb-1">Score Pondéré</p>
              <p className="font-display text-4xl font-bold text-white">
                {analysis.totalScore?.weighted} <span className="text-xl text-cap-text-muted">/ {analysis.totalScore?.maxWeighted}</span>
              </p>
            </div>
            <div className="text-center">
              <p className="text-cap-text-muted text-sm mb-1">Pourcentage</p>
              <p className={`font-display text-5xl font-bold ${verdict.color}`}>{analysis.totalScore?.percent?.toFixed(0)}%</p>
            </div>
            <div className="text-center">
              <p className={`font-bold text-sm ${verdict.color}`}>{verdict.label}</p>
            </div>
          </div>
        </div>

        {/* Eliminatory Alerts */}
        {analysis.eliminatoryAlerts?.length > 0 && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 space-y-2" data-testid="eliminatory-alerts">
            <h3 className="text-red-400 font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Alertes éliminatoires déclenchées
            </h3>
            {analysis.eliminatoryAlerts.map(id => {
              const section = allSections.find(s => s.id === id);
              return (
                <p key={id} className="text-red-300 text-sm">
                  {id} — {section?.titre} : score inférieur à {section?.eliminatoire}/{section?.max_pts} pts
                </p>
              );
            })}
          </div>
        )}

        {/* Radar Chart */}
        <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6">
          <h3 className="font-display text-lg font-bold text-white mb-4">Radar des sections</h3>
          <div className="h-80">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="section" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 10 }} />
                <Radar name="Score %" dataKey="score" stroke="#00e5a0" fill="#00e5a0" fillOpacity={0.2} strokeWidth={2} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value) => [`${value}%`, 'Score']}
                  labelFormatter={(label) => radarData.find(d => d.section === label)?.name || label}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Section Table */}
        <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h3 className="font-display text-lg font-bold text-white">Récapitulatif par section</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="section-recap-table">
              <thead>
                <tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                  <th className="py-3 px-4">Section</th>
                  <th className="py-3 px-4">Score brut</th>
                  <th className="py-3 px-4">Score pondéré</th>
                  <th className="py-3 px-4">%</th>
                  <th className="py-3 px-4">Appréciation</th>
                </tr>
              </thead>
              <tbody>
                {activeSections.map(s => {
                  const sc = analysis.sectionScores?.[s.id] || {};
                  const apprec = getAppréciation(sc.percent || 0);
                  const isElim = analysis.eliminatoryAlerts?.includes(s.id);
                  return (
                    <tr key={s.id} className={`border-b border-white/5 ${isElim ? 'bg-red-500/5' : ''}`}>
                      <td className="py-3 px-4">
                        <span className="text-cap-text-muted text-xs font-mono mr-2">{s.id}</span>
                        <span className="text-white text-sm">{s.titre}</span>
                        {isElim && <AlertTriangle className="w-3.5 h-3.5 text-red-400 inline ml-2" />}
                      </td>
                      <td className="py-3 px-4 text-white font-mono text-sm">{sc.raw || 0}/{sc.maxRaw || 0}</td>
                      <td className="py-3 px-4 text-white font-mono text-sm">{sc.weighted || 0}/{sc.maxWeighted || 0}</td>
                      <td className="py-3 px-4 text-white font-mono text-sm">{sc.percent || 0}%</td>
                      <td className="py-3 px-4 text-sm">{apprec.icon} {apprec.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI Synthesis */}
        <div className="bg-cap-bg-elevated border border-white/5 rounded-2xl p-6" data-testid="ai-synthesis-block">
          <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#00e5a0' }}>
            <Bot className="w-5 h-5" style={{ color: '#00e5a0' }} /> Synthèse IA
          </h3>
          {aiSynthesis ? (
            <SynthesisRenderer text={aiSynthesis} />
          ) : (
            <div className="text-center py-6">
              <Button onClick={generateSynthesis} disabled={aiLoading}
                className="bg-cap-blue/20 hover:bg-cap-blue/30 text-cap-blue border border-cap-blue/30" data-testid="generate-synthesis-btn">
                {aiLoading ? (
                  <><div className="w-4 h-4 border-2 border-cap-blue border-t-transparent rounded-full animate-spin mr-2" /> Génération en cours...</>
                ) : (
                  <><Bot className="w-4 h-4 mr-2" /> Générer la synthèse IA</>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mt-8 pb-8">
        <Button onClick={handleSave} className="bg-cap-emerald hover:bg-cap-emerald-hover text-black font-bold" data-testid="save-analysis-btn">
          <Save className="w-4 h-4 mr-2" /> Sauvegarder l'analyse
        </Button>
        <Button variant="outline" onClick={onRedo} className="border-white/10 text-cap-text-secondary" data-testid="redo-analysis-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refaire l'analyse
        </Button>
        <Button variant="outline" onClick={handleExportPDF} className="border-white/10 text-cap-text-secondary" data-testid="export-pdf-btn">
          <Download className="w-4 h-4 mr-2" /> Exporter en PDF
        </Button>
      </div>
    </div>
  );
};

export default AnalyseResults;
